import {
  ServerlessProject,
  StatusServerlessProject,
  GetServerlessProjectsResponse,
  GetServerlessProjectByIdResponse,
  ServerlessTask,
  StatusServerlessTask,
  TypeServerless,
  ListServerlessTasksResponse,
  GetServerlessInstancePricingResponse,
  ServerlessEndpoint,
  GetServerlessEndpointByIdResponse,
} from '@/service/serverless-gpu/type';

// Demo workspace ID (same as gpu-instance, imported from there)
// Using DEMO_WORKSPACE_ID from gpu-instance.ts for consistency

// Mock Serverless Projects
export const mockServerlessProjects: ServerlessProject[] = [
  {
    project_id: 'demo-project-001',
    project_name: 'Demo Image Classification',
    instance_type: 'NVIDIA A100 80GB',
    status: StatusServerlessProject.Active,
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
    updated_at: new Date(Date.now() - 3600000).toISOString(), // 1 hour ago
    has_endpoint: true,
  },
  {
    project_id: 'demo-project-002',
    project_name: 'Demo LLM Inference',
    instance_type: 'NVIDIA L4',
    status: StatusServerlessProject.Active,
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days ago
    updated_at: new Date(Date.now() - 7200000).toISOString(), // 2 hours ago
    has_endpoint: true,
  },
  {
    project_id: 'demo-project-003',
    project_name: 'Demo Video Processing',
    instance_type: 'NVIDIA T4',
    status: StatusServerlessProject.Active,
    created_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
    updated_at: new Date(Date.now() - 86400000).toISOString(), // 1 day ago
    has_endpoint: false,
  },
];

export const mockGetServerlessProjectsResponse: GetServerlessProjectsResponse = {
  projects: mockServerlessProjects,
};

export const getMockServerlessProjectById = (
  projectId: string
): GetServerlessProjectByIdResponse | null => {
  const project = mockServerlessProjects.find((p) => p.project_id === projectId);
  if (!project) return null;
  return {
    project,
    details: 'Project retrieved successfully (Demo)',
    status: 'success',
  };
};

// Mock Serverless Tasks
export const mockServerlessTasks: ServerlessTask[] = [
  {
    id: 'demo-task-001',
    name: 'Image batch processing',
    status: StatusServerlessTask.Completed,
    created_at: new Date(Date.now() - 3600000).toISOString(),
    trigger_type: TypeServerless.Function,
    project_id: 'demo-project-001',
  },
  {
    id: 'demo-task-002',
    name: 'Model training run',
    status: StatusServerlessTask.Running,
    created_at: new Date(Date.now() - 1800000).toISOString(),
    trigger_type: TypeServerless.Spot,
    project_id: 'demo-project-001',
  },
  {
    id: 'demo-task-003',
    name: 'API endpoint request',
    status: StatusServerlessTask.ComplateSuccess,
    created_at: new Date(Date.now() - 7200000).toISOString(),
    trigger_type: TypeServerless.Server,
    project_id: 'demo-project-002',
  },
  {
    id: 'demo-task-004',
    name: 'Video transcoding job',
    status: StatusServerlessTask.Pending,
    created_at: new Date(Date.now() - 300000).toISOString(),
    trigger_type: TypeServerless.Manual,
    project_id: 'demo-project-003',
  },
  {
    id: 'demo-task-005',
    name: 'Failed inference',
    status: StatusServerlessTask.ComplateFailed,
    created_at: new Date(Date.now() - 86400000).toISOString(),
    trigger_type: TypeServerless.Function,
    project_id: 'demo-project-001',
  },
];

export const mockListServerlessTasksResponse: ListServerlessTasksResponse = {
  tasks: mockServerlessTasks,
};

export const getMockServerlessTasksByProjectId = (
  projectId?: string
): ListServerlessTasksResponse => {
  if (!projectId) {
    return { tasks: mockServerlessTasks };
  }
  return {
    tasks: mockServerlessTasks.filter((task) => task.project_id === projectId),
  };
};

// Mock Instance Pricing
export const mockServerlessInstancePricing: GetServerlessInstancePricingResponse['data'] = [
  {
    instance_id: 'a100-80gb',
    instance_type: 'NVIDIA A100 80GB',
    price_per_hour: 2.5,
    spot_price_per_hour: 1.25,
    storage_price_per_month: 0.1,
    instance_provider: 'Float16',
    instance_region: 'Asia Pacific',
  },
  {
    instance_id: 'a100-40gb',
    instance_type: 'NVIDIA A100 40GB',
    price_per_hour: 1.8,
    spot_price_per_hour: 0.9,
    storage_price_per_month: 0.1,
    instance_provider: 'Float16',
    instance_region: 'Asia Pacific',
  },
  {
    instance_id: 'l4',
    instance_type: 'NVIDIA L4',
    price_per_hour: 0.8,
    spot_price_per_hour: 0.4,
    storage_price_per_month: 0.1,
    instance_provider: 'Float16',
    instance_region: 'Asia Pacific',
  },
  {
    instance_id: 't4',
    instance_type: 'NVIDIA T4',
    price_per_hour: 0.5,
    spot_price_per_hour: 0.25,
    storage_price_per_month: 0.1,
    instance_provider: 'Float16',
    instance_region: 'Asia Pacific',
  },
];

// Mock Endpoint
export const getMockServerlessEndpoint = (
  projectId: string
): GetServerlessEndpointByIdResponse => ({
  endpoint: `https://demo-api.float16.cloud/v1/${projectId}`,
  api_key: 'demo-api-key-xxxx-xxxx-xxxx',
  deployed_at: new Date(Date.now() - 86400000).toISOString(),
  project_id: projectId,
  is_active: true,
});

// Mock Storage Files
export const mockServerlessStorageFiles = [
  {
    path_name: '/models/model.pt',
    size: 1024 * 1024 * 500, // 500MB
    type: 'file',
    last_modified: new Date(Date.now() - 86400000).toISOString(),
  },
  {
    path_name: '/data/dataset.csv',
    size: 1024 * 1024 * 50, // 50MB
    type: 'file',
    last_modified: new Date(Date.now() - 172800000).toISOString(),
  },
  {
    path_name: '/logs/',
    size: 0,
    type: 'directory',
    last_modified: new Date(Date.now() - 3600000).toISOString(),
  },
];

// Mock metrics
export const mockServerlessMetrics = {
  total_tasks: 150,
  total_usage: 245.5, // hours
  total_cost: 489.0,
  cost_breakdown: {
    storage: 15.0,
    on_demand: 324.0,
    spot: 150.0,
  },
  usage_breakdown: {
    on_demand: 145.5,
    spot: 100.0,
  },
};
